import cn from 'classnames';
import {Form, Formik} from 'formik';
import React, {useMemo} from 'react';
import {PropTypes} from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {PromptProps} from '../../model/promptPropTypes';
import {getFormControlConfig} from './DynamicForm.helpers';
import {DynamicFormToFormikAdapter} from './DynamicFormToFormikAdapter';

import './DynamicForm.scss';

// logical container to allow for easy recursion
export const DynamicFormBody = ({prompts, level, className = '', key = ''}) => (
  <div key={key} className={className}>
    {prompts &&
      prompts.map(prompt =>
        getFormControlConfig(prompt.type, level).getComponent({
          ...prompt,
          className: 'dynamic-form__control_' + (level >= 2 ? 'max' : level),
        })
      )}
  </div>
);

const formatValue = (value, isMultiSelect) =>
  isMultiSelect && typeof value === 'string' ? [value] : value;

export function getDefaultValues(prompts, rawDefaults = {}) {
  let defaults = {...rawDefaults};

  if (Array.isArray(prompts) && rawDefaults && typeof rawDefaults === 'object') {
    defaults = prompts.reduce((defaults, {key, type, prefilledValue, multipleSelect}) => {
      const rawDefault = rawDefaults[key];
      const isMultiSelect = type === 'MULTIPLE_CHOICES' && multipleSelect;

      if (typeof rawDefault === 'object' || prefilledValue !== undefined) {
        const baseValue =
          typeof rawDefault === 'object'
            ? prefilledValue ?? rawDefault.value
            : prefilledValue;

        defaults[key] = {
          ...(typeof rawDefault === 'object' ? rawDefault : {key}),
          value: formatValue(baseValue, isMultiSelect),
        };
      }

      return defaults;
    }, defaults);
  }

  return defaults;
}

export const DynamicForm = ({
  title,
  onBlur,
  prompts,
  className,
  onSubmit,
  renderButtons,
  buttonPlacement,
  defaults: rawDefaults,
}) => {
  const defaults = useMemo(() => {
    return getDefaultValues(prompts, rawDefaults);
  }, [prompts, rawDefaults]);

  return (
    <DynamicFormToFormikAdapter prompts={prompts} defaults={defaults} onSubmit={onSubmit}>
      {(prompts, initialValues, initialErrors, onSubmit) => (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          initialErrors={initialErrors}
          validateOnMount={Object.values(initialErrors).length === 0}
          onSubmit={onSubmit}
        >
          {({isSubmitting, isValid, dirty, values}) => (
            <Form
              noValidate
              className={cn('dynamic-form', className)}
              onBlur={() => {
                if (onBlur) {
                  onBlur(values);
                }
              }}
            >
              {buttonPlacement === 'top' &&
                (!renderButtons
                  ? null
                  : renderButtons(isSubmitting, isValid, dirty, values))}
              {title && (
                <h2 className="dynamic-form__title">
                  <FormattedMessage id={title} />
                </h2>
              )}
              <div className="dynamic-form__body">
                <DynamicFormBody prompts={prompts} level={0} />
              </div>
              {buttonPlacement === 'bottom' &&
                (!renderButtons
                  ? null
                  : renderButtons(isSubmitting, isValid, dirty, values))}
            </Form>
          )}
        </Formik>
      )}
    </DynamicFormToFormikAdapter>
  );
};

DynamicForm.propTypes = {
  title: PropTypes.string,
  prompts: PromptProps,
  className: PropTypes.string,
  onSubmit: PropTypes.func,
  defaults: PropTypes.object,
  buttonPlacement: PropTypes.string,
  renderButtons: PropTypes.func,
};

DynamicForm.defaultProps = {
  prompts: [],
  defaults: {},
  className: null,
  formReference: null,
  buttonPlacement: 'bottom',
  onSubmit: () => {},
  onBlur: () => {},
  renderButtons: () => {},
};
